import type { CSSProperties } from "react";

type LayerPanelProps = {
  isOpen: boolean;
  panelStyle?: CSSProperties;
  onActivate?: () => void;
  layers: Array<{
    name: string;
    label?: string;
    visible: boolean;
    locked: boolean;
    canMoveUp: boolean;
    canMoveDown: boolean;
    onToggleVisible: () => void;
    onToggleLocked: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
  }>;
};

export function LayerPanel({ isOpen, panelStyle, onActivate, layers }: LayerPanelProps) {
  if (!isOpen) return null;

  return (
    <aside
      className="absolute bottom-[5.5rem] left-32 z-20 flex w-[340px] flex-col gap-3 rounded-[6px] border border-[rgba(74,58,31,0.12)] bg-[rgba(245,240,230,0.98)] p-3 shadow-[0_20px_40px_rgba(58,45,20,0.14)] backdrop-blur-[10px]"
      style={panelStyle}
      onMouseDownCapture={onActivate}
      onFocusCapture={onActivate}
    >
      <h3 className="m-0 text-[11px] font-bold uppercase tracking-[0.08em] text-[#4b3e27]">
        Layer
      </h3>

      <div className="flex flex-col gap-2">
        {layers.map((layer) => (
          <div
            key={layer.name}
            className="flex items-center justify-between rounded-md border border-[rgba(74,58,31,0.12)] bg-white px-3 py-2"
          >
            <div className="min-w-0">
              <div className="truncate text-[12px] font-semibold text-[#1f2937]">{layer.label ?? layer.name}</div>
            </div>

            <div className="ml-3 flex items-center gap-2">
              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.9)] text-[#6c5b3d] transition enabled:active:scale-110 disabled:opacity-35"
                aria-label={`Bring ${layer.label ?? layer.name} forward`}
                title={`Bring ${layer.label ?? layer.name} forward`}
                disabled={!layer.canMoveUp}
                onClick={layer.onMoveUp}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                  <path d="M12 5v14M6 11l6-6 6 6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.9)] text-[#6c5b3d] transition enabled:active:scale-110 disabled:opacity-35"
                aria-label={`Send ${layer.label ?? layer.name} backward`}
                title={`Send ${layer.label ?? layer.name} backward`}
                disabled={!layer.canMoveDown}
                onClick={layer.onMoveDown}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                  <path d="M12 19V5M6 13l6 6 6-6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <button
                type="button"
                className={`inline-flex h-9 w-9 items-center justify-center rounded-md border transition active:scale-110 ${
                  layer.locked
                    ? 'border-[rgba(29,92,171,0.24)] bg-[rgba(29,92,171,0.08)] text-[#1d5cab]'
                    : 'border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.9)] text-[#6c5b3d]'
                }`}
                aria-label={layer.locked ? `Unlock ${layer.label ?? layer.name}` : `Lock ${layer.label ?? layer.name}`}
                title={layer.locked ? `Unlock ${layer.label ?? layer.name}` : `Lock ${layer.label ?? layer.name}`}
                onClick={layer.onToggleLocked}
              >
                {layer.locked ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                    <rect x="5" y="11" width="14" height="10" rx="2" strokeWidth="1.8" />
                    <path d="M8 11V8a4 4 0 1 1 8 0v3" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                    <rect x="5" y="11" width="14" height="10" rx="2" strokeWidth="1.8" />
                    <path d="M16 11V8a4 4 0 0 0-8 0" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                )}
              </button>

              <button
                type="button"
                className={`inline-flex h-9 w-9 items-center justify-center rounded-md border transition active:scale-110 ${
                  layer.visible
                    ? 'border-[rgba(29,92,171,0.24)] bg-[rgba(29,92,171,0.08)] text-[#1d5cab]'
                    : 'border-[rgba(74,58,31,0.12)] bg-[rgba(255,255,255,0.9)] text-[#6c5b3d]'
                }`}
                aria-label={layer.visible ? `Hide ${layer.label ?? layer.name}` : `Show ${layer.label ?? layer.name}`}
                title={layer.visible ? `Hide ${layer.label ?? layer.name}` : `Show ${layer.label ?? layer.name}`}
                onClick={layer.onToggleVisible}
              >
                {layer.visible ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                    <path
                      d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />
                    <circle cx="12" cy="12" r="3" strokeWidth="1.8" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-none stroke-current">
                    <path
                      d="M3 3 21 21M10.6 10.7a3 3 0 0 0 4.1 4.1M9.9 5.2A11.8 11.8 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-4.2 4.7M6 6.3C3.6 8 2 12 2 12s3.5 6 10 6c1 0 2-.1 2.8-.4"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}
